import { createAction } from '@reduxjs/toolkit';
import type {
  AnnotatorRelationship,
  API,
  ErrorObject,
  MRule,
  MRuleConfigNode,
  NormalizedAnnotator,
  NormalizedResource,
  Rule,
  RuleTestCasesSummary,
  UUID,
} from 'types';

export type FetchSingleRulePayload = {
  ruleId: UUID;
};

export const fetchSingleRule = createAction<FetchSingleRulePayload>('RULE/FETCH');
export const fetchSingleRuleRequest = createAction('RULE/FETCH_REQUEST');
export const fetchSingleRuleFailure = createAction<ErrorObject>('RULE/FETCH_FAILURE');
export const fetchSingleRuleSuccess = createAction<MRule>('RULE/FETCH_SUCCESS');

export type FetchAllRulesPayload = {
  name?: string;
  limit?: string;
  // eslint-disable-next-line camelcase
  customer_uuid?: string;
};

export const fetchAllRulesDebounced = createAction<FetchAllRulesPayload>('RULES/DEBOUNCED_FETCH');
export const fetchAllRules = createAction<FetchAllRulesPayload>('RULES/FETCH');
export const fetchAllRulesWithSummary = createAction<FetchAllRulesPayload>('RULES_SUMMARY/FETCH');
export const fetchFilterRules = createAction<FetchAllRulesPayload>('RULES/FETCH_FILTER_REQUEST');

export const fetchAllRulesRequest = createAction('RULES/FETCH_REQUEST');
export const fetchAllRulesFailure = createAction<ErrorObject>('RULES/FETCH_FAILURE');
export const fetchAllRulesSuccess =
  createAction<API.WrappedAPIResponse<Rule>>('RULES/FETCH_SUCCESS');

export type DeleteRulePayload = {
  ruleId: UUID;
};

export const saveRelationshipsSuccess = createAction<
  NormalizedResource<Omit<AnnotatorRelationship, 'configId'>>
>('RELATIONSHIP/SAVE_SUCCESS');
export const fetchRelationshipsSuccess = createAction<
  NormalizedResource<Omit<AnnotatorRelationship, 'configId'>>
>('RELATIONSHIP/FETCH_SUCCESS');

export type UpdateConfigGroupsPayload = {
  parentId: UUID;
  newConfigId: UUID;
};
export const updateConfigGroups = createAction<UpdateConfigGroupsPayload>(
  'CONFIG/UPDATE_CONFIG_GROUPS'
);
export const createRuleConfig = createAction<NormalizedResource<MRuleConfigNode>>('CONFIG/CREATE');
export const saveRuleConfigSuccess =
  createAction<NormalizedResource<MRuleConfigNode>>('CONFIG/SAVE_SUCCESS');
export const fetchRuleConfigSuccess =
  createAction<NormalizedResource<MRuleConfigNode>>('CONFIG/FETCH_SUCCESS');

export type AddAnnotationMatcherToRulePayload = {
  annotator: NormalizedAnnotator;
  rule: MRule;
  dropIndex?: number;
};

export type FetchRuleChangelogType = {
  ruleId: UUID;
  auditlogUuid: UUID;
  version: string;
};

export const fetchRuleChangelog = createAction<FetchRuleChangelogType>('RULE/FETCH_CHANGELOG');

export type FetchRuleTestCasesPayload = {
  rule_uuids: UUID;
};

export const fetchTestCaseSummaryRule = createAction<FetchRuleTestCasesPayload>(
  'TEST_CASE_SUMMARY_RULE/FETCH'
);
export const fetchTestCaseSummaryRuleRequest = createAction('TEST_CASE_SUMMARY_RULE/FETCH_REQUEST');
export const fetchTestCaseSummaryRuleSuccess = createAction<RuleTestCasesSummary>(
  'TEST_CASE_SUMMARY_RULE/FETCH_SUCCESS'
);
export const fetchTestCaseSummaryRuleFailure = createAction<ErrorObject>(
  'TEST_CASE_SUMMARY_RULE/FETCH_FAILURE'
);

export type PublisRuleVersion = {
  ruleId: UUID;
  version: string;
};

export const publishRuleVersion = createAction<PublisRuleVersion>('RULES/PUBLISH_VERSION');
export const publishRuleVersionRequest = createAction('RULES/PUBLISH_VERSION_REQUEST');
export const publishRuleVersionSuccess = createAction<MRule>('RULES/PUBLISH_VERSION_SUCCESS');
export const publishRuleVersionFailure = createAction<ErrorObject>('RULES/PUBLISH_VERSION_FAILURE');

export type UndoRuleVersion = {
  ruleId: UUID;
};

export const undoRuleVersion = createAction<UndoRuleVersion>('RULES/UNDO_VERSION');
export const undoRuleVersionRequest = createAction('RULES/UNDO_VERSION_REQUEST');
export const undoRuleVersionSuccess = createAction<MRule>('RULES/UNDO_VERSION_SUCCESS');
export const undoRuleVersionFailure = createAction<ErrorObject>('RULES/UNDO_VERSION_FAILURE');
